import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  Lock,
  Database,
  Mail,
  Key,
} from "lucide-react";
import { toast } from "sonner";
import PortalView from "./PortalView";
import OTPVerification from "@/components/OTPVerification";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animated loading overlay component
const PortalLoadingOverlay = ({ portalName, primaryColor }) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background">
      {/* Animated background pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-pulse"
              style={{
                width: `${Math.random() * 300 + 100}px`,
                height: `${Math.random() * 300 + 100}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                background: `radial-gradient(circle, ${primaryColor || '#059669'}40 0%, transparent 70%)`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${Math.random() * 3 + 2}s`,
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6">
        {/* Animated logo/icon */}
        <div className="relative mb-8">
          {/* Outer spinning ring */}
          <div 
            className="w-24 h-24 rounded-full border-4 border-t-transparent animate-spin"
            style={{ borderColor: `${primaryColor || '#059669'}30`, borderTopColor: 'transparent' }}
          />
          {/* Inner pulsing circle */}
          <div 
            className="absolute inset-3 rounded-full animate-pulse flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor || '#059669'}20` }}
          >
            <Database 
              className="w-8 h-8 animate-bounce" 
              style={{ color: primaryColor || '#059669' }}
            />
          </div>
          {/* Orbiting dots */}
          <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
            <div 
              className="absolute w-3 h-3 rounded-full -top-1 left-1/2 -translate-x-1/2"
              style={{ backgroundColor: primaryColor || '#059669' }}
            />
          </div>
          <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s', animationDelay: '1s' }}>
            <div 
              className="absolute w-2 h-2 rounded-full -bottom-1 left-1/2 -translate-x-1/2 opacity-60"
              style={{ backgroundColor: primaryColor || '#059669' }}
            />
          </div>
        </div>
        
        {/* Portal name */}
        {portalName && (
          <h2 
            className="text-2xl font-bold mb-4"
            style={{ color: primaryColor || '#059669' }}
          >
            {portalName}
          </h2>
        )}
        
        {/* Loading text */}
        <p className="text-xl font-medium text-foreground mb-2">
          Taihoa
        </p>
        <p className="text-muted-foreground text-lg">
          We are preparing your Portal for mahi.
        </p>
        
        {/* Progress bar */}
        <div className="mt-8 w-64 h-1.5 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full animate-pulse"
            style={{ 
              backgroundColor: primaryColor || '#059669',
              animation: 'loading-progress 2s ease-in-out infinite'
            }}
          />
        </div>
      </div>
      
      {/* CSS for the loading progress animation */}
      <style>{`
        @keyframes loading-progress {
          0% { width: 0%; margin-left: 0%; }
          50% { width: 60%; margin-left: 20%; }
          100% { width: 0%; margin-left: 100%; }
        }
      `}</style>
    </div>
  );
};

export default function PublicPortal() {
  const { shareToken } = useParams();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preparingPortal, setPreparingPortal] = useState(false); // New state for loading overlay

  // Auth state
  const [authRequired, setAuthRequired] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [portalUser, setPortalUser] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [authCheckLoading, setAuthCheckLoading] = useState(true);
  
  // 2FA state
  const [showOTP, setShowOTP] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [verifyingOTP, setVerifyingOTP] = useState(false);

  // Check if authentication is required
  useEffect(() => {
    checkAuthRequired();
  }, [shareToken]);

  const checkAuthRequired = async () => {
    try {
      const response = await axios.get(`${API}/public/portal/${shareToken}/auth-check`);
      setAuthRequired(response.data.auth_required);
      
      if (response.data.auth_required) {
        // Check if we have a saved token
        const savedToken = localStorage.getItem(`portal_user_token_${shareToken}`);
        if (savedToken) {
          await verifyToken(savedToken);
        } else {
          // No saved token - need to show login form
          // Fetch portal data for branding display on login page
          try {
            const portalResponse = await axios.get(`${API}/public/portal/${shareToken}`);
            setPortal(portalResponse.data);
          } catch (e) {
            // Portal might not be fully public, that's ok - we just won't have branding
          }
          setLoading(false);
        }
      } else {
        // No auth required, fetch portal directly
        await fetchPortal();
      }
    } catch (error) {
      setError("Portal not found");
    } finally {
      setAuthCheckLoading(false);
    }
  };

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(
        `${API}/portal/${shareToken}/authenticated`,
        { headers: { 'X-Portal-User-Token': token } }
      );
      setPortal(response.data);
      setPortalUser(response.data.user);
      setIsAuthenticated(true);
      toast.success(`Welcome back, ${response.data.user?.name || response.data.user?.email}!`);
    } catch (error) {
      localStorage.removeItem(`portal_user_token_${shareToken}`);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchPortal = async () => {
    try {
      const response = await axios.get(`${API}/public/portal/${shareToken}`);
      setPortal(response.data);
    } catch (error) {
      setError("Failed to load portal");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/portal-auth/${shareToken}/login`, {
        email: loginEmail,
        password: loginPassword
      });
      
      if (response.data.requires_otp) {
        // Show OTP verification screen
        setOtpToken(response.data.otp_token);
        setOtpEmail(response.data.email);
        setShowOTP(true);
        toast.success("Verification code sent to your email!");
      } else {
        // Direct login (fallback if 2FA disabled)
        const { portal_user_token } = response.data;
        localStorage.setItem(`portal_user_token_${shareToken}`, portal_user_token);
        setPreparingPortal(true); // Show loading overlay
        await verifyToken(portal_user_token);
        toast.success(`Welcome!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleVerifyOTP = async (otp) => {
    setVerifyingOTP(true);
    setOtpError(null);
    
    try {
      const response = await axios.post(`${API}/portal-auth/${shareToken}/verify-otp`, {
        otp: otp,
        otp_token: otpToken
      });
      
      const { portal_user_token, user_name, user_email } = response.data;
      localStorage.setItem(`portal_user_token_${shareToken}`, portal_user_token);
      setPreparingPortal(true); // Show loading overlay
      await verifyToken(portal_user_token);
      toast.success(`Welcome, ${user_name || user_email}!`);
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid verification code";
      setOtpError(message);
    } finally {
      setVerifyingOTP(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await axios.post(`${API}/portal-auth/${shareToken}/resend-otp`, {
        otp_token: otpToken
      });
      setOtpToken(response.data.otp_token);
      toast.success("New verification code sent!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to resend code";
      toast.error(message);
    }
  };

  const handleBackToLogin = () => {
    setShowOTP(false);
    setOtpToken(null);
    setOtpEmail("");
    setOtpError(null);
  };

  const handleLogout = async () => {
    const savedToken = localStorage.getItem(`portal_user_token_${shareToken}`);
    if (savedToken) {
      try {
        await axios.post(
          `${API}/portal-auth/${shareToken}/logout`,
          {},
          { headers: { 'X-Portal-User-Token': savedToken } }
        );
      } catch (error) {
        // Ignore logout errors
      }
    }
    localStorage.removeItem(`portal_user_token_${shareToken}`);
    setIsAuthenticated(false);
    setPortalUser(null);
    setPortal(null);
    toast.success("Logged out successfully");
  };

  // Get branding colors
  const primaryColor = portal?.branding?.primary_color || "#059669";

  // Loading state
  if (authCheckLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">{error}</h2>
          <p className="text-muted-foreground">Please check the URL and try again.</p>
        </div>
      </div>
    );
  }

  // OTP Verification screen
  if (showOTP && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
            <OTPVerification
              email={otpEmail}
              otpToken={otpToken}
              onVerify={handleVerifyOTP}
              onResend={handleResendOTP}
              onBack={handleBackToLogin}
              isLoading={verifyingOTP}
              error={otpError}
              primaryColor={primaryColor}
            />
          </div>
        </div>
      </div>
    );
  }

  // Login form for authenticated portals
  if (authRequired && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                {portal?.branding?.logo_url ? (
                  <img src={portal.branding.logo_url} alt="Logo" className="h-8" />
                ) : (
                  <Database className="w-8 h-8" style={{ color: primaryColor }} />
                )}
              </div>
              <h1 className="text-2xl font-bold text-foreground">
                {portal?.branding?.company_name || portal?.name || 'Portal Login'}
              </h1>
              <p className="text-muted-foreground mt-2">Sign in to access your portal</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">Password</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                style={{ backgroundColor: primaryColor }}
                disabled={loginLoading}
              >
                {loginLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                Sign In
              </Button>

              <div className="text-center mt-3">
                <Link
                  to={`/p/${shareToken}/forgot-password`}
                  className="text-sm hover:underline"
                  style={{ color: primaryColor }}
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Powered by Paeora
          </p>
        </div>
      </div>
    );
  }

  // Render the full PortalView for authenticated users or public portals
  return (
    <>
      {/* Loading overlay during portal preparation */}
      {preparingPortal && (
        <PortalLoadingOverlay 
          portalName={portal?.name}
          primaryColor={portal?.branding?.primary_color}
        />
      )}
      <PortalView 
        isPublicView={true}
        publicPortalData={portal}
        publicShareToken={shareToken}
        publicUserToken={localStorage.getItem(`portal_user_token_${shareToken}`)}
        portalUser={portalUser}
        onLogout={handleLogout}
        onReady={() => setPreparingPortal(false)} // Hide overlay when records loaded
      />
    </>
  );
}
